import React, {Fragment, useEffect} from 'react';
import {Link, useParams} from "react-router-dom";
import {useApi} from "../../../providers/ApiProvider";
import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
import BusinessesModel from "../../../models/BusinessesModel";
import BusinessViewHeader from "../BusinessViewHeader";
import SimpleCardContainer from "../../ui/SimpleCardContainer";
import Chart from "react-apexcharts";
import BusinessViewMenu from "../BusinessViewMenu";
import BusinessesInsightsChart from "./BusinessesInsightsChart";
import {useTranslation} from "react-i18next";
import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";
import StrHelper from "../../../helpers/StrHelper";
import BusinessesReportingModel from "../../../models/BusinessesReportingModel";
import ButtonWithLoading from "../../layout/ui/ButtonWithLoading";

export default function BusinessesInsightsReporting(props) {
    const {language} = useMyTranslation();
    const { t} = useTranslation();
    const {locationId} = useParams();
    const apiContext = useApi();
    const businessesModel = new BusinessesModel(apiContext.api);
    const businessesReportingModel = new BusinessesReportingModel(apiContext.api, locationId);
    const breadcrumbs = useBreadcrumbs();
    const [loading, setLoading] = React.useState('');

    const repeatTypes = {
        'daily': t('Daily'),
        'weekly': t('Weekly'),
        'biweekly': t('Biweekly'),
        'monthly': t('Monthly')
    };

    const [startDate, setStartDate] = React.useState(new Date().toISOString().slice(0,10));
    const [repeatType, setRepeatType] = React.useState('daily');
    const [recipients, setRecipients] = React.useState('');
    const [notificationParamsVisible, setNotificationParamsVisible] = React.useState(false);
    const [subject, setSubject] = React.useState('');
    const [content, setContent] = React.useState('');
    const [isActive, setIsActive] = React.useState(false);
    const [sendTime, setSendTime] = React.useState('08:00');

    const onProfileRefresh = (profile, lastUpdated) => {
        return true;
    }
    
    useEffect(() => {
        businessesModel.view(locationId).then((response) => {
            if (response !== false) {
                breadcrumbs.update({
                    title: response.data.title,
                    breadcrumbs: [
                        {label: t('Businesses List'), url: '/businesses'},
                        {label: response.data.title, url: '/businesses/'+locationId},
                        {label: t('Insights'), url: '/businesses/'+locationId+'/insights'},
                        {label: t('Reporting')}
                    ]
                });

                document.title = t('Reporting')+' | '+response.data.title+' | LocalBoost';
            }
        }).finally(() => {
        });

        getReporting();
    }, [language]);


    const getReporting = () => {
        businessesReportingModel.view().then((response) => {
            if (response) {
                setStartDate(response.date_start ? response.date_start.substring(0, 10) : new Date().toISOString().slice(0,10));
                setRepeatType(response.repeat_type || 'daily');
                setRecipients(response.recipients);
                if (response.notification_parameters) {
                    if (response.notification_parameters.subject) {
                        setSubject(response.notification_parameters.subject);
                    }
                    if (response.notification_parameters.content) {
                        setContent(response.notification_parameters.content);
                    }
                } else {
                    setSubject('');
                    setContent('');
                }
                setIsActive(response.is_active);
                if (response.send_time) {
                    setSendTime(response.send_time);
                }
            } else {
                setStartDate(new Date().toISOString().slice(0,10));
                setRepeatType('daily');
                setRecipients('');
                setSubject('');
                setContent('');
                setIsActive(false);
                setSendTime('08:00');
            }
        });
    }


    const saveReporting = () => {
          setLoading('save');

          const formData = new FormData();
          formData.append('date_start', startDate);
          formData.append('repeat_type', repeatType);
          formData.append('recipients', recipients);
          formData.append('send_time', sendTime);
          formData.append('notification_parameters', JSON.stringify({subject: subject, content: content}));
          formData.append('is_active', isActive ? '1' : '0');

          businessesReportingModel.update(formData).then((response) => {
              if (response) {
                  getReporting();
              }
          }).finally(() => {
              setLoading('');
          });
      }


      const cancelReporting = () => {
        setLoading('cancel');

        businessesReportingModel.delete().then((response) => {
            if (response) {
                getReporting();
            }
        }).finally(() => {
            if (loading === 'cancel') {
                setLoading('');
            }
        });
    }


    return (
        <>
            <div id="insightsWrap" >				
				<div id="businessHeader">
					<BusinessViewHeader
						locationId={locationId}
						onProfileRefresh={onProfileRefresh}
					/>
				</div>

				<div id="businessMenu" data-html2canvas-ignore="true">
					<BusinessViewMenu locationId={locationId} active="insights" />
				</div>

				<div className={'row'}>
					<div className={'col-lg-8 d-flex align-items-stretch'}>
                        <SimpleCardContainer
                            title={t('Reporting')}
                            loading={false}
                        >

                            <div className="row">
                                <div className="col-lg-3">
                                    <div className={'form-group mb-3'}>
                                        <label className="form-label fw-semibold mb-2">{t('Startdatum')}</label>

                                        <input id={'cd-name'} type="date" className={'form-control'} value={startDate} onChange={(e) => {
                                            setStartDate(e.target.value)
                                        }}/>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className={'form-group mb-3'}>
                                        <label className="form-label fw-semibold mb-2">{t('Wiederholungsintervall')}</label>

                                        <select id={'cd-button'} className={'form-control form-select'} value={repeatType} onChange={(e) => {
                                            setRepeatType(e.target.value)
                                        }}>
                                            {Object.keys(repeatTypes).map((key) => {
                                                return <option value={key} key={key}>{repeatTypes[key]}</option>
                                            })}
                                        </select>
                                      </div>
                                  </div>
                                  <div className="col-lg-3">
                                      <div className={'form-group mb-3'}>
                                          <label className="form-label fw-semibold mb-2">{t('Versandzeit')}</label>

                                          <input type="time" className={'form-control'} value={sendTime} onChange={(e) => {
                                              setSendTime(e.target.value)
                                          }}/>
                                      </div>
                                  </div>
                              </div>

                            <div className={'form-group mb-3'}>
                                <label className="form-label fw-semibold mb-2">{t('Empfänger')}</label>

                                <input id={'cd-name'} type="text" className={'form-control'} value={recipients} onChange={(e) => {
                                    setRecipients(e.target.value)
                                }}/>
                            </div>

                            <button className="btn btn-sm btn-light-primary text-primary mb-3"
                                    onClick={(e) => setNotificationParamsVisible(!notificationParamsVisible)}>{t('E-Mail-Einstellungen anpassen')}
                            </button>

                            {notificationParamsVisible && (<>

                                <div className="row">
                                    <div className="col-lg-8">
                                        <div className={'form-group mb-3'}>
                                            <label className="form-label fw-semibold mb-2">{t('E-Mail-Betreff')}</label>

                                            <input id={'cd-name'} type="text" className={'form-control'} value={subject} onChange={(e) => {
                                                setSubject(e.target.value)
                                            }}/>
                                        </div>

                                        <div className={'form-group mb-3'}>
                                            <label className="form-label fw-semibold mb-2">{t('E-Mail-Nachricht')}</label>

                                            <textarea id={'cd-name'} className={'form-control'} value={content} onChange={(e) => {
                                                setContent(e.target.value)
                                            }}></textarea>
                                        </div>
                                    </div>
                                </div>
                            </>)}

                            <div className="form-check mb-3">
                                <input
                                    type="checkbox"
                                    id={'closed'}
                                    name={'closed'}
                                    className={'form-check-input'}
                                    checked={isActive}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        setIsActive(!isActive);
                                    }}
                                />
                                <label
                                    className="form-check-label cursor-pointer"
                                    htmlFor={'closed'}>{t('Automatischen Versand aktivieren')}</label>
                            </div>

                            <div className="d-flex gap-3">
                                <ButtonWithLoading
                                    className={'btn btn-primary'}
                                    onClick={() => {
                                        saveReporting();
                                    }}
                                    noOriginalIconClassName={true}
                                    loading={loading === 'save'}
                                    label={t('Einstellungen speichern')}
                                />

                                <ButtonWithLoading
                                    className={'btn btn-light-danger text-danger'}
                                    onClick={cancelReporting}
                                    noOriginalIconClassName={true}
                                    loading={loading === 'cancel'}
                                    label={t('Berichterstattung deaktivieren')}
                                />
                            </div>

                        </SimpleCardContainer>
                    </div>
                    <div className={'col-lg-4'}>
                        <strong>{t('Startdatum')}:</strong> {startDate}<br />
                        <strong>{t('Wiederholung')}:</strong> {repeatTypes[repeatType]}<br />
                        <strong>{t('Versandzeit')}:</strong> {sendTime}<br />
                        <strong>{t('Empfänger')}:</strong> {recipients}<br />
                        <strong>{t('E-Mail-Betreff')}:</strong> {subject}<br />
                        <strong>{t('E-Mail-Nachricht')}:</strong> {content}<br />
                        <strong>{t('Versand aktiv')}:</strong> {isActive ? t('Ja') : t('Nein')}
                    </div>
                </div>
            </div>
        </>
    );
}